import { Component, OnDestroy, OnInit } from '@angular/core';
import { RootState } from '@next/core-lib/app';
import { FilterAction } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { FormatLocationPipe } from '@next/core-lib/pipes';
import { SettingsService, SettingsStorage } from '@next/core-lib/settings';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import {
  DeliveryCategories,
  DeliveryColumnTypes,
} from '../../../delivery-data/delivery-data.model';
import { DeliveryDataService } from '../../../delivery-data/services/delivery-data.service';
import {
  clearDeliveryTotalsRequest,
  printDeliveryReportRequest,
  loadDeliveryTotalsRequest,
} from '../../../delivery-data/store/delivery-data.actions';
import { selectDeliveryTotals } from '../../../delivery-data/store/delivery-data.selectors';
import { HierarchicalData } from '../../../shared/hierarchical-data/hierarchical-data.model';
import { settingsContextName, SettingsKeys } from '../../../shared/models/shared.models';
import { PageHierarchicalDataFilterComponent } from '../../../shared/page-hierarchical-data-filter/page-hierarchical-data-filter.component';
import { FormatBrsNumberPipe } from '../../../shared/pipes/format-brs-number.pipe';
import { selectCustomerBrsNumbers, selectCustomerKvkNumbers } from '../../../store/selectors';

@Component({
  selector: 'app-mineral-totals',
  templateUrl: './mineral-totals.component.html',
  providers: [FormatBrsNumberPipe, FormatLocationPipe],
  imports: [PageHierarchicalDataFilterComponent, TranslatePipe],
})
export class MineralTotalsComponent implements OnInit, OnDestroy {
  public readonly totalsTableConfig = {
    prefix: 'mineral.mineral-totals.',
    localeService: this.localeService,
    columns: [
      DeliveryColumnTypes.description,
      DeliveryColumnTypes.productTitle,
      DeliveryColumnTypes.totalQuantityMineral,
      DeliveryColumnTypes.phosphorusTotal,
      DeliveryColumnTypes.nitrogenTotal,
    ],
    digitsInfo: {
      [DeliveryColumnTypes.totalQuantityMineral]: '1.0-0',
      [DeliveryColumnTypes.phosphorusTotal]: '1.0-0',
      [DeliveryColumnTypes.nitrogenTotal]: '1.0-0',
    },
    hideUnits: true,
    groupSortOrder: {
      '': 0,
      'RUNDVEE-MELK': 1,
      'RUNDVEE-VLEES': 2,
      VARKENS: 3,
      KIPPEN: 4,
      'RUW-ENKELV-VOER': 5,
    },
  };

  public readonly filterActions: FilterAction[] = [
    {
      icon: 'file_download',
      viewValue: 'Excel',
      value: 'xlsx',
    },
    {
      icon: 'file_download',
      viewValue: 'PDF',
      value: 'pdf',
    },
    {
      icon: 'file_download',
      viewValue: 'CSV',
      value: 'csv',
    },
  ];

  public readonly headings = this.deliveryDataService.getHeadings(
    this.totalsTableConfig.columns,
    'mineral.mineral-totals.',
  );

  public readonly exportFilename = `${this.localeService.translate(
    'mineral.mineral-totals.fileName',
  )}`;

  public data$: Observable<HierarchicalData[]>;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly settingsService: SettingsService,
    private readonly deliveryDataService: DeliveryDataService,
    private readonly formatBrsNumberPipe: FormatBrsNumberPipe,
  ) {}

  ngOnInit(): void {
    this.data$ = this.store
      .select(selectDeliveryTotals(this.totalsTableConfig))
      .pipe(takeUntil(this.destroy$));
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearDeliveryTotalsRequest());

    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadData(): void {
    this.store.dispatch(loadDeliveryTotalsRequest({ category: DeliveryCategories.mineral }));
  }

  public printReport = () =>
    combineLatest([
      this.settingsService.getSettings({
        contextName: settingsContextName,
        storage: SettingsStorage.PlatformSession,
        key: SettingsKeys.dateRange,
      }),
      this.settingsService.getSettings({
        contextName: settingsContextName,
        storage: SettingsStorage.PlatformSession,
        key: SettingsKeys.brsNumber,
      }),
      this.store.select(selectCustomerBrsNumbers),
      this.store.select(selectCustomerKvkNumbers),
      this.store.select(UserSelectors.selectCustomer),
      this.data$,
    ])
      .pipe(
        take(1),
        map(
          ([
            dateRange,
            filterBrsNumberResult,
            customerBrsNumbers,
            customerKvkNumbers,
            customer,
            data,
          ]) => {
            const startDate = new Date((dateRange?.[0].data as { startDate: string })?.startDate);
            const endDate = new Date((dateRange?.[0].data as { endDate: string })?.endDate);
            const filterBrsNumber = filterBrsNumberResult[0].data as string;
            const brsNumberToDisplay = customerBrsNumbers.find(
              (customerBrsNumber) => customerBrsNumber.brsNumber === filterBrsNumber,
            );
            const kvkNumberToDisplay =
              customerKvkNumbers.find((kvkNumber) =>
                brsNumberToDisplay.relatedLocations.some(
                  (brsLoc) => brsLoc.id === kvkNumber.relatedLocation.id,
                ),
              ) ?? customerKvkNumbers[0];

            this.store.dispatch(
              printDeliveryReportRequest({
                report: {
                  title: this.localeService.translate('mineral.mineral-totals.pfdExportTitle'),
                  subTitle: `${this.localeService.translate(
                    'mineral.report.sub-title',
                  )} ${this.deliveryDataService.formatDateTime(new Date())}`,
                  fileName: this.deliveryDataService.formatExportFileName(
                    this.localeService.translate('mineral.mineral-totals.fileName'),
                    customer.externalId,
                    startDate,
                    endDate,
                  ),
                  legend: {
                    [this.localeService.translate('mineral.report.legend.customer')]:
                      `${customer.name} (${customer.externalId})`,
                    ...(kvkNumberToDisplay
                      ? {
                          [this.localeService.translate('mineral.report.legend.kvk-number')]:
                            kvkNumberToDisplay.kvkNumber,
                        }
                      : {}),
                    [this.localeService.translate('mineral.report.legend.brs-number')]:
                      this.formatBrsNumberPipe.transform(brsNumberToDisplay),
                    [this.localeService.translate('mineral.report.legend.start-date')]:
                      this.deliveryDataService.formatDateTime(startDate),
                    [this.localeService.translate('mineral.report.legend.end-date')]:
                      this.deliveryDataService.formatDateTime(endDate),
                  },
                  tableHeadings:
                    this.totalsTableConfig?.columns?.map((column) =>
                      this.localeService.translate(`${this.totalsTableConfig.prefix}${column}`),
                    ) ?? [],
                  tableFooter: [
                    this.localeService.translate('mineral.report.disclaimer1'),
                    this.localeService.translate('mineral.report.disclaimer2'),
                  ],

                  contactInformation: {
                    email: this.localeService.translate('mineral.report.contact.email'),
                    phoneNumber: this.localeService.translate(
                      'mineral.report.contact.phone-number',
                    ),
                  },
                  rows: this.deliveryDataService.mapHierarchicalDataToPdfRows(data),
                },
              }),
            );
          },
        ),
      )
      .subscribe();
}
